import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "./utils.ts";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium transition-all duration-300 ease-out " +
    "disabled:pointer-events-none disabled:opacity-50 " +
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 " +
    "active:scale-[0.97] " +
    "[&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 " +
    "rounded-xl shadow-sm",

  {
    variants: {
      variant: {
        /** ✨ Default Modern Gradient */
        default:
          "bg-gradient-to-r bg-[#A8322D] " +
          "text-white shadow-md hover:shadow-xl hover:brightness-110"+
          "opacity-80 hover:opacity-100",

        /** 🔥 Destructive Modern */
        destructive:
          "bg-gradient-to-r from-red-500 to-orange-500 text-white " +
          "hover:shadow-lg hover:brightness-110",

        /** 🌙 Outline Glass */
        outline:
          "border border-white/20 bg-white/10 backdrop-blur-md text-foreground " +
          "hover:bg-white/20 hover:shadow-md",

        /** 🌸 Soft Secondary */
        secondary:
          "bg-muted text-foreground hover:bg-muted/70 hover:shadow-md",

        /** 👻 Ghost Modern */
        ghost:
          "hover:bg-white/10 hover:backdrop-blur-md hover:shadow-sm",

        /** 🔗 Link */
        link:
          "text-primary underline-offset-4 hover:underline hover:text-purple-500",

        /** 💎 NEW: Neon Variant */
        neon:
          "bg-black text-white border border-purple-500 " +
          "shadow-[0_0_15px_rgba(168,85,247,0.6)] " +
          "hover:shadow-[0_0_25px_rgba(236,72,153,0.8)]",
      },

      size: {
        default: "h-10 px-6 text-sm",
        sm: "h-9 px-4 text-xs",
        lg: "h-12 px-8 text-base",
        icon: "h-10 w-10 p-0",
      },
    },

    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
